import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import React, { useState, useEffect, useRef } from 'react';
import shallowEqual from 'shallowequal';
import Store from './Store';
import mapStateToPropsDefault from './utils/mapStateToProps';
import { isStateObject } from './lib';
import { getFromStore, saveToStore } from './storage';

var ReactLightState =
/*#__PURE__*/
function () {
  function ReactLightState(initState, storeName, _temp) {
    var _ref = _temp === void 0 ? {} : _temp,
        _ref$storageName = _ref.storageName,
        storageName = _ref$storageName === void 0 ? null : _ref$storageName,
        _ref$getFromStorage = _ref.getFromStorage,
        getFromStorage = _ref$getFromStorage === void 0 ? getFromStore : _ref$getFromStorage,
        _ref$saveToStorage = _ref.saveToStorage,
        saveToStorage = _ref$saveToStorage === void 0 ? saveToStore : _ref$saveToStorage;

    this.initState = initState;
    this.storeName = storeName;
    this.options = {
      storageName: storageName,
      getFromStorage: getFromStorage,
      saveToStorage: saveToStorage
    };

    if (this.options.storageName) {
      this.store = new Store(this.options.getFromStorage(this.options.storageName) || initState);
    } else {
      this.store = new Store(initState);
    }

    this.setState = this.setState.bind(this);
    this.dispatch = this.dispatch.bind(this);
    this.getState = this.getState.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.resetState = this.resetState.bind(this);
    this.withLight = this.withLight.bind(this);
    this.Light = this.Light.bind(this);
    this.useStore = this.useStore.bind(this);
  }

  var _proto = ReactLightState.prototype;

  _proto.setState = function setState(data, cb) {
    var _this = this;

    if (cb === void 0) {
      cb = function cb() {};
    }

    if (typeof data === 'function') {
      return _asyncToGenerator(
      /*#__PURE__*/
      _regeneratorRuntime.mark(function _callee() {
        var newData;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return data(_this.getState());

              case 2:
                newData = _context.sent;

                _this.setState(newData, cb);

              case 4:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    } else {
      var newData = _extends({}, this.getState(), data);

      this.store.setData(newData);
      cb(newData);

      if (this.options.storageName) {
        this.options.saveToStorage(this.options.storageName, newData);
      }
    }
  };

  _proto.dispatch = function dispatch(func, cb) {
    var _this2 = this;

    if (typeof func !== 'function') {
      this.setState(func, cb);
      return;
    }

    return _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee2() {
      var data;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return func(_this2.dispatch, _this2.getState());

            case 2:
              data = _context2.sent;

              _this2.dispatch(data, cb);

            case 4:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }))();
  };

  _proto.getState = function getState(key) {
    if (key) {
      return this.store.getData()[key];
    } else {
      return this.store.getData();
    }
  };

  _proto.subscribe = function subscribe(cb) {
    this.store.subscribe(cb);
    return cb;
  };

  _proto.unsubscribe = function unsubscribe(cb) {
    return this.store.unsubscribe(cb);
  }
  /**
   * Reset the `LightState` data to `initState` data.
   */
  ;

  _proto.resetState = function resetState() {
    this.store.setData(this.initState);
  }
  /**
   * A HOC, like `connect` in `react-redux`
   * @param {function} mapStateToProps An object to map `lightState` value to your props
   */
  ;

  _proto.withLight = function withLight(mapStateToProps) {
    if (mapStateToProps === void 0) {
      mapStateToProps = mapStateToPropsDefault;
    }

    var store = this.store;
    var storeName = this.storeName;
    return function (Component) {
      return (
        /*#__PURE__*/
        function (_React$Component) {
          _inheritsLoose(_class, _React$Component);

          function _class(props) {
            var _this3;

            _this3 = _React$Component.call(this, props) || this;
            _this3.state = {
              data: store.getData()
            };
            return _this3;
          }

          var _proto2 = _class.prototype;

          _proto2.componentDidMount = function componentDidMount() {
            var _this4 = this;

            this.subed = store.subscribe(function (data) {
              _this4.setState({
                data: data
              });
            });
          };

          _proto2.componentWillUnmount = function componentWillUnmount() {
            store.unsubscribe(this.subed);
          };

          _proto2.render = function render() {
            return (
              /*#__PURE__*/
              React.createElement(Component, _extends({}, this.props, mapStateToProps(this.state.data, storeName)))
            );
          };

          return _class;
        }(React.Component)
      );
    };
  };

  _proto.Light = function Light(_ref4) {
    var mapStateToProps = _ref4.mapStateToProps,
        children = _ref4.children;
    return (
      /*#__PURE__*/
      React.createElement(SetupLight, {
        initState: this.initState,
        store: this.store,
        storeName: this.storeName,
        mapStateToProps: mapStateToProps,
        children: children
      })
    );
  };

  _proto.useStore = function useStore(mapStateToProps) {
    var _this5 = this;

    if (mapStateToProps === void 0) {
      mapStateToProps = mapStateToPropsDefault;
    }

    var _useState = useState(mapStateToProps(this.store.getData())),
        state = _useState[0],
        setState = _useState[1];

    var _useState2 = useState(null),
        error = _useState2[0],
        setError = _useState2[1]; // As our effect only fires on mount and unmount it won't have the state
    // changes visible to it, therefore we use a mutable ref to track this.


    var stateRef = useRef(state); // Helps avoid firing of events when unsubscribed, i.e. unmounted

    var isActive = useRef(true); // Tracks when a hooked component is unmounting

    var unmounted = useRef(false);

    if (error) {
      throw error;
    }

    useEffect(function () {
      isActive.current = true;

      var calculateState = function calculateState() {
        if (!isActive.current) {
          return;
        }

        try {
          var newState = mapStateToProps(_this5.store.getData());

          if (newState === stateRef.current || isStateObject(newState) && isStateObject(stateRef.current) && shallowEqual(newState, stateRef.current)) {
            // Do nothing
            return;
          }

          stateRef.current = newState;
          setState(stateRef.current);
        } catch (err) {
          isActive.current = false;
          setTimeout(function () {
            if (!unmounted.current && !isActive.current) {
              setError(err);
            }
          }, 200); // give a window of opportunity
        }
      };

      var unsubscribe = _this5.store.subscribe(calculateState);

      return _this5.store.unsubscribe(unsubscribe);
    });
    return state;
  };

  return ReactLightState;
}();

ReactLightState.prototype.connect = ReactLightState.prototype.withLight;

var SetupLight =
/*#__PURE__*/
function (_React$Component2) {
  _inheritsLoose(SetupLight, _React$Component2);

  function SetupLight(props) {
    var _this6;

    _this6 = _React$Component2.call(this, props) || this;
    _this6.state = {
      data: _this6.props.store.getData()
    };
    return _this6;
  }

  var _proto3 = SetupLight.prototype;

  _proto3.componentDidMount = function componentDidMount() {
    var _this7 = this;

    this.subed = this.props.store.subscribe(function (data) {
      _this7.setState({
        data: data
      });
    });
  };

  _proto3.componentWillUnmount = function componentWillUnmount() {
    this.props.store.unsubscribe(this.subed);
  };

  _proto3.render = function render() {
    var mapStateToProps = this.props.mapStateToProps || mapStateToPropsDefault;
    var storeName = this.props.storeName;
    var renderedChildren = this.props.children(mapStateToProps(this.state.data, storeName));
    return renderedChildren && React.Children.only(renderedChildren);
  };

  return SetupLight;
}(React.Component);

export default ReactLightState;